import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { canManageAdminWorkspace } from '../../lib/services/adminUsers'
import type { AdminUserSummary } from '../../lib/services/adminUsers'
import { getAdminUsers, getAdminUser, replaceAdminUserFacilities } from '../../lib/services/adminUsers'
import { createFacility, getFacilities } from '../../lib/services/facilities'
import type { FacilityInput, FacilityOption } from '../../lib/services/facilities'
import { Building2, ChevronDown, ChevronUp, Plus, Users, X } from 'lucide-react'

const FACILITY_TYPE_OPTIONS = [
  'Ambulatory Surgery Center',
  'Hospital',
  'Clinic',
  'Long-Term Care',
  'Rehabilitation',
  'Other',
] as const

const ACCREDITATION_FRAMEWORK_OPTIONS = [
  { value: 'cms', label: 'CMS only' },
  { value: 'aaahc', label: 'AAAHC' },
  { value: 'tjc', label: 'TJC' },
  { value: 'quad_a', label: 'QUAD A' },
  { value: 'achc', label: 'ACHC' },
  { value: 'state', label: 'State only' },
  { value: 'other', label: 'Other' },
] as const

function accreditationFrameworkLabel(value: string | null | undefined): string {
  return ACCREDITATION_FRAMEWORK_OPTIONS.find((option) => option.value === value)?.label ?? 'CMS only'
}

const EMPTY_FORM: FacilityInput = {
  facility_name: '',
  facility_type: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  accreditation_framework: 'cms',
}

function FacilityCreateForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState<FacilityInput>({ ...EMPTY_FORM })
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: FacilityInput) => createFacility(input),
    onSuccess: async () => {
      setForm({ ...EMPTY_FORM })
      await queryClient.invalidateQueries({ queryKey: ['facility-setup-facilities'] })
      onCreated()
    },
  })

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await mutation.mutateAsync(form)
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Facility name *"
          value={form.facility_name}
          onChange={(e) => setForm({ ...form, facility_name: e.target.value })}
          required
        />
        <select
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={form.facility_type ?? ''}
          onChange={(e) => setForm({ ...form, facility_type: e.target.value })}
        >
          <option value="">Select type</option>
          {FACILITY_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Address"
          value={form.address ?? ''}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="City"
          value={form.city ?? ''}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">
          Accreditation framework
        </label>
        <select
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={form.accreditation_framework ?? 'cms'}
          onChange={(e) => setForm({ ...form, accreditation_framework: e.target.value })}
        >
          {ACCREDITATION_FRAMEWORK_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-500">Used to select the report-writing posture. CMS is the fallback when no accreditor is selected.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="State *"
          value={form.state}
          onChange={(e) => setForm({ ...form, state: e.target.value })}
          required
        />
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Zip code"
          value={form.zip_code ?? ''}
          onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
        />
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary text-sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating...' : 'Create Facility'}
        </button>
        {mutation.error && (
          <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
        )}
      </div>
    </form>
  )
}

function FacilityUserAssignment({
  facility,
  allUsers,
}: {
  facility: FacilityOption
  allUsers: AdminUserSummary[]
}) {
  const [expanded, setExpanded] = useState(false)
  const [assignedUserIds, setAssignedUserIds] = useState<string[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadAssignments = async () => {
    if (assignedUserIds !== null) return
    setIsLoading(true)
    setError(null)
    try {
      // Load each user's facility assignments to find who is assigned to this facility
      const assigned: string[] = []
      for (const user of allUsers) {
        try {
          const detail = await getAdminUser(user.user_id)
          if (detail.facility_assignments.some((a) => a.facility_id === facility.facility_id)) {
            assigned.push(user.user_id)
          }
        } catch {
          // Skip users we cannot load
        }
      }
      setAssignedUserIds(assigned)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleExpand = async () => {
    const next = !expanded
    setExpanded(next)
    if (next) {
      await loadAssignments()
    }
  }

  const toggleUser = (userId: string) => {
    if (!assignedUserIds) return
    setAssignedUserIds(
      assignedUserIds.includes(userId)
        ? assignedUserIds.filter((id) => id !== userId)
        : [...assignedUserIds, userId],
    )
  }

  const saveAssignments = async () => {
    if (!assignedUserIds) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      // For each user, update their facility assignments to include/exclude this facility
      for (const user of allUsers) {
        const detail = await getAdminUser(user.user_id)
        const currentFacilityIds = detail.facility_assignments.map((a) => a.facility_id)
        const shouldBeAssigned = assignedUserIds.includes(user.user_id)
        const isAssigned = currentFacilityIds.includes(facility.facility_id)

        if (shouldBeAssigned && !isAssigned) {
          await replaceAdminUserFacilities(user.user_id, [...currentFacilityIds, facility.facility_id])
        } else if (!shouldBeAssigned && isAssigned) {
          await replaceAdminUserFacilities(
            user.user_id,
            currentFacilityIds.filter((id) => id !== facility.facility_id),
          )
        }
      }
      setSuccess('Assignments saved.')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="v11-dashboard-card">
      <button
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-3">
          <Building2 size={16} className="text-[var(--brand-primary)]" />
          <div>
            <p className="text-sm font-bold text-slate-900">{facility.facility_name}</p>
            <p className="text-xs text-slate-500">
              {[facility.facility_type, facility.city, facility.state].filter(Boolean).join(' | ') || 'No details'}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-slate-500">
              Framework: {accreditationFrameworkLabel(facility.accreditation_framework)}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <Users size={12} />
            <span>User Assignments</span>
          </div>

          {isLoading && <p className="mt-2 text-sm text-slate-500">Loading assignments...</p>}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

          {!isLoading && assignedUserIds !== null && (
            <>
              <div className="mt-2 grid gap-1">
                {allUsers.map((user) => (
                  <label
                    key={user.user_id}
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={assignedUserIds.includes(user.user_id)}
                      onChange={() => toggleUser(user.user_id)}
                    />
                    <span>{user.email}</span>
                    <span className="ml-auto text-xs text-slate-400">{user.role}</span>
                  </label>
                ))}
                {allUsers.length === 0 && (
                  <p className="text-sm text-slate-500">No users available for assignment.</p>
                )}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <button
                  className="btn-primary text-sm"
                  disabled={saving}
                  onClick={saveAssignments}
                >
                  {saving ? 'Saving...' : 'Save Assignments'}
                </button>
                {success && <p className="text-sm text-emerald-700">{success}</p>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function FacilitySetupPage() {
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'
  const canManage = canManageAdminWorkspace(role)
  const [showCreateForm, setShowCreateForm] = useState(false)

  const facilitiesQuery = useQuery({
    queryKey: ['facility-setup-facilities'],
    queryFn: getFacilities,
    enabled: canManage,
  })

  const usersQuery = useQuery({
    queryKey: ['facility-setup-users'],
    queryFn: getAdminUsers,
    enabled: canManage,
  })

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Facility setup is restricted to Admin role.
      </div>
    )
  }

  const facilities = facilitiesQuery.data ?? []
  const users = usersQuery.data ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Facility Setup</h1>
            <p className="mt-1 text-sm text-slate-600">
              Create facilities and manage user assignments for your organization.
            </p>
          </div>
          <button
            className="btn-primary flex items-center gap-1.5 px-4 py-2 text-sm"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? (
              <>
                <X size={14} />
                Cancel
              </>
            ) : (
              <>
                <Plus size={14} />
                New Facility
              </>
            )}
          </button>
        </div>
      </header>

      {showCreateForm && (
        <section className="v11-section p-5">
          <h2 className="section-title">Create Facility</h2>
          <FacilityCreateForm onCreated={() => setShowCreateForm(false)} />
        </section>
      )}

      <section className="v11-section p-5">
        <h2 className="section-title">Facilities</h2>

        {facilitiesQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading facilities...</p>}
        {facilitiesQuery.error && (
          <p className="mt-2 text-sm text-red-600">{(facilitiesQuery.error as Error).message}</p>
        )}
        {usersQuery.error && (
          <p className="mt-2 text-sm text-red-600">{(usersQuery.error as Error).message}</p>
        )}

        {!facilitiesQuery.isLoading && !facilitiesQuery.error && facilities.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No facilities found. Create one to get started.</p>
        )}

        <div className="mt-3 grid gap-3">
          {facilities.map((facility) => (
            <FacilityUserAssignment
              key={facility.facility_id}
              facility={facility}
              allUsers={users}
            />
          ))}
        </div>
      </section>
    </div>
  )
}
